// Gauging Inspection — Go/No-Go style for bore/plug/snap gauges.
// Big visual PASS/FAIL feedback, tolerance band, part + serial tracking.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Stack, Chip, Divider, IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RuleIcon from '@mui/icons-material/Rule';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const STORAGE_KEY = 'quality_inspections_gauging';
const GAUGE_TYPES = ['Plug Gauge', 'Bore Gauge', 'Snap Gauge', 'Ring Gauge', 'Thread Gauge'];

const GaugingScreen = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [components, setComponents] = useState([]);
  const [history, setHistory] = useState([]);

  const [partCode, setPartCode] = useState('');
  const [gaugeType, setGaugeType] = useState('Plug Gauge');
  const [feature, setFeature] = useState('OD ø');
  const [nominal, setNominal] = useState(20.00);
  const [upperTol, setUpperTol] = useState(0.05);
  const [lowerTol, setLowerTol] = useState(0.02);
  const [actual, setActual] = useState('');
  const [serial, setSerial] = useState('');
  const [operator, setOperator] = useState(localStorage.getItem('firstName')?.replace(/"/g, '') || 'Operator');

  useEffect(() => {
    (async () => {
      try {
        const r = await customerbasedshift(cid, 'component');
        const v = r?.[0]?.value || [];
        setComponents(Array.isArray(v) ? v : []);
      } catch (e) { setComponents([]); }
      try {
        const r = await customerbasedshift(cid, STORAGE_KEY);
        const v = r?.[0]?.value || [];
        setHistory(Array.isArray(v) ? v : []);
      } catch (e) { setHistory([]); }
    })();
  }, [cid]);

  const actualNum = Number(actual);
  const upperLimit = nominal + Number(upperTol);
  const lowerLimit = nominal - Number(lowerTol);
  const result = actual !== '' && !Number.isNaN(actualNum)
    ? (actualNum >= lowerLimit && actualNum <= upperLimit ? 'PASS' : 'FAIL')
    : null;

  const partOptions = components.slice(0, 30).map(c => ({
    code: c.component_number || '',
    name: c.component_name || '',
  }));

  const save = async () => {
    if (!partCode || actual === '') return;
    const entry = {
      id: 'QC-G-' + Date.now(),
      ts: Date.now(),
      partCode, gaugeType, feature,
      nominal: Number(nominal), upperTol: Number(upperTol), lowerTol: Number(lowerTol),
      actual: actualNum, result, serial, operator,
    };
    const next = [entry, ...history].slice(0, 200);
    setHistory(next);
    try { await shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE'); }
    catch (e) { console.error('save failed', e); }
    // Reset measurement, keep part + gauge
    setActual('');
    setSerial('');
  };

  const tone = result === 'PASS' ? '#16a34a' : result === 'FAIL' ? '#dc2626' : '#94a3b8';
  const toneBg = result === 'PASS' ? '#dcfce7' : result === 'FAIL' ? '#fee2e2' : '#f3f4f6';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <RuleIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>Gauging — Go / No-Go Inspection</Typography>
        <Chip label={`${history.length} records`} size="small" sx={{ ml: 'auto' }} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Entry card */}
        <Card sx={{ p: 2.5, flex: 1, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 2, letterSpacing: 1 }}>
            ENTRY
          </Typography>
          <Stack spacing={2}>
            <TextField select label="Component" value={partCode} onChange={(e) => setPartCode(e.target.value)} size="small">
              {partOptions.map(p => (
                <MenuItem key={p.code} value={p.code}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8 }}>{p.code}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>{p.name}</span>
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1.5}>
              <TextField select label="Gauge Type" value={gaugeType} onChange={(e) => setGaugeType(e.target.value)} size="small" sx={{ flex: 1 }}>
                {GAUGE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Feature" value={feature} onChange={(e) => setFeature(e.target.value)} size="small" sx={{ flex: 1 }} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Nominal (mm)" type="number" value={nominal} onChange={(e) => setNominal(e.target.value)} size="small" sx={{ flex: 1 }} />
              <TextField label="Upper +" type="number" value={upperTol} onChange={(e) => setUpperTol(e.target.value)} size="small" sx={{ flex: 1 }} inputProps={{ step: 0.01 }} />
              <TextField label="Lower −" type="number" value={lowerTol} onChange={(e) => setLowerTol(e.target.value)} size="small" sx={{ flex: 1 }} inputProps={{ step: 0.01 }} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Serial / Batch" value={serial} onChange={(e) => setSerial(e.target.value)} size="small" sx={{ flex: 1 }} />
              <TextField label="Operator" value={operator} onChange={(e) => setOperator(e.target.value)} size="small" sx={{ flex: 1 }} />
            </Stack>
            <TextField label="ACTUAL READING (mm)" type="number" value={actual} onChange={(e) => setActual(e.target.value)} inputProps={{ step: 0.001 }}
              sx={{ '& input': { fontSize: 24, fontWeight: 800, fontFamily: 'monospace', textAlign: 'center' } }} />
          </Stack>
        </Card>

        {/* Result panel */}
        <Card sx={{ p: 2.5, width: { xs: '100%', md: 380 }, borderRadius: 3, border: `3px solid ${tone}`, bgcolor: toneBg, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: tone, letterSpacing: 1.2, mb: 1 }}>
            RESULT
          </Typography>
          <Typography sx={{ fontSize: 64, fontWeight: 900, color: tone, lineHeight: 1, textAlign: 'center', my: 2 }}>
            {result || '—'}
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2, mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>TOLERANCE BAND</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: 'text.primary' }}>
              {lowerLimit.toFixed(3)}  ≤  X  ≤  {upperLimit.toFixed(3)} mm
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2, mb: 2 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>YOUR READING</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 22, color: tone }}>
              {actual !== '' ? Number(actual).toFixed(3) : '—'}
            </Typography>
          </Box>
          <Button variant="contained" color={result === 'PASS' ? 'success' : result === 'FAIL' ? 'error' : 'inherit'}
            onClick={save} disabled={!partCode || actual === ''}
            sx={{ py: 1.5, fontWeight: 800, fontSize: 15, textTransform: 'none', mt: 'auto' }}>
            Save inspection
          </Button>
        </Card>
      </Stack>

      {/* Recent history */}
      <Card sx={{ mt: 2, p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          RECENT INSPECTIONS
        </Typography>
        {history.length === 0 && (
          <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>No inspections yet. Enter one above.</Typography>
        )}
        {history.slice(0, 20).map(h => (
          <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Chip label={h.result} size="small" sx={{
              bgcolor: h.result === 'PASS' ? '#dcfce7' : '#fee2e2',
              color: h.result === 'PASS' ? '#14532d' : '#7a0000', fontWeight: 800, minWidth: 70,
            }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'text.primary' }}>{h.partCode}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{h.gaugeType} · {h.feature}</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: 'text.primary', ml: 'auto' }}>{Number(h.actual).toFixed(3)}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 100, textAlign: 'right' }}>
              {new Date(h.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            <IconButton size="small" onClick={() => {
              const next = history.filter(x => x.id !== h.id);
              setHistory(next);
              shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE').catch(() => {});
            }}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default GaugingScreen;
